import React, {
  useState,
  useEffect,
  useMemo,
} from "react";

import "../Styles/GuyanaLottery.css";

import {
  Landmark,
  Plus,
  Search,
  Ticket,
  Wallet,
  CircleDollarSign,
  TrendingUp,
  Trash2,
  CheckCircle2,
  XCircle,
} from "lucide-react";

import api from "../api";

import { toast } from "react-toastify";

const normalizeTxn = (txn) => ({
  ...txn,
  soldDate: txn.sold_date,
  paidDate: txn.paid_date,
});

const GuyanaLottery = () => {

  /* ========================================
      STATE
  ======================================== */

  const [transactions, setTransactions] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [searchTerm, setSearchTerm] =
    useState("");

  const [showModal, setShowModal] =
    useState(false);

  const [formData, setFormData] =
    useState({
      date: "",
      value: "",
      code: "",
      name: "",
      sold: false,
      paid: false,
      soldDate: "",
      paidDate: "",
    });

  /* ========================================
      FETCH
  ======================================== */

  useEffect(() => {

    const fetchData = async () => {

      try {

        setLoading(true);

        const data =
          await api.getLottery();

        setTransactions(
          data.map(normalizeTxn)
        );

      } catch (error) {

        console.error(error);

        toast.error(
          "Failed to load lottery data."
        );

      } finally {

        setLoading(false);

      }
    };

    fetchData();

  }, []);

  /* ========================================
      FORM
  ======================================== */

  const handleChange = (e) => {

    const {
      name,
      value,
    } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });
  };

  /* ========================================
      CREATE
  ======================================== */

  const addTransaction =
    async () => {

      if (
        !formData.date ||
        !formData.value ||
        !formData.code ||
        !formData.name
      ) {

        toast.warn(
          "Complete all fields."
        );

        return;
      }

      try {

        const newTxn =
          await api.createLottery(
            formData
          );

        if (newTxn) {

          setTransactions([
            normalizeTxn(newTxn),
            ...transactions,
          ]);

          toast.success(
            "Packed transaction created."
          );

          setFormData({
            date: "",
            value: "",
            code: "",
            name: "",
            sold: false,
            paid: false,
            soldDate: "",
            paidDate: "",
          });

          setShowModal(false);

        }

      } catch (error) {

        console.error(error);

        toast.error(
          "Failed to create transaction."
        );

      }
    };

  /* ========================================
      TOGGLE
  ======================================== */

  const toggleField =
    async (id, field) => {

      const txn =
        transactions.find(
          (t) => t.id === id
        );

      if (!txn) return;

      const newValue =
        !txn[field];

      const now =
        new Date()
          .toISOString()
          .split("T")[0];

      const optimistic = {
        ...txn,

        [field]: newValue,

        [`${field}Date`]:
          newValue
            ? now
            : null,
      };

      setTransactions(
        transactions.map((t) =>
          t.id === id
            ? optimistic
            : t
        )
      );

      try {

        const updatedTxn =
          await api.toggleLotteryField(
            id,
            field,
            newValue
          );

        if (updatedTxn) {

          setTransactions(
            transactions.map((t) =>
              t.id === id
                ? normalizeTxn(
                    updatedTxn
                  )
                : t
            )
          );

        }

      } catch (error) {

        console.error(error);

        toast.error(
          "Update failed."
        );

      }
    };

  /* ========================================
      DELETE
  ======================================== */

  const handleDelete =
    async (id) => {

      if (
        !window.confirm(
          "Delete this packed transaction?"
        )
      )
        return;

      try {

        const success =
          await api.deleteLottery(
            id
          );

        if (success) {

          setTransactions(
            transactions.filter(
              (txn) =>
                txn.id !== id
            )
          );

          toast.success(
            "Transaction deleted."
          );

        }

      } catch (error) {

        console.error(error);

        toast.error(
          "Delete failed."
        );

      }
    };

  /* ========================================
      FILTERED
  ======================================== */

  const filteredTransactions =
    useMemo(() => {

      return transactions.filter(
        (txn) => {

          const term =
            searchTerm.toLowerCase();

          return (
            txn.code
              ?.toLowerCase()
              .includes(term) ||

            txn.name
              ?.toLowerCase()
              .includes(term)
          );
        }
      );

    }, [
      transactions,
      searchTerm,
    ]);

  /* ========================================
      STATS
  ======================================== */

  const totalValue =
    transactions.reduce(
      (sum, txn) =>
        sum +
        Number(txn.value),
      0
    );

  const soldValue =
    transactions
      .filter(
        (txn) => txn.sold
      )
      .reduce(
        (sum, txn) =>
          sum +
          Number(txn.value),
        0
      );

  const atHandValue =
    totalValue - soldValue;

  const dueValue =
    transactions
      .filter(
        (txn) => !txn.paid
      )
      .reduce(
        (sum, txn) =>
          sum +
          Number(txn.value),
        0
      );

  /* ========================================
      LOADING
  ======================================== */

  if (loading) {

    return (

      <div className="lottery-modern-loading">

        <div className="lottery-modern-loader">

          <div className="lottery-modern-loader-ring" />

          <div className="lottery-modern-loader-ring-two" />

          <div className="lottery-modern-loader-center">

            <Ticket size={42} />

          </div>

        </div>

        <h2>
          Loading Packed Transactions
        </h2>

        <p>
          Preparing ticket data,
          balances and lottery
          activity...
        </p>

      </div>
    );
  }

  /* ========================================
      UI
  ======================================== */

  return (

    <div className="lottery-modern-page">

      {/* HERO */}

      <div className="lottery-modern-hero">

        <div className="lottery-modern-hero-left">

          <div className="lottery-modern-pill">

            <Landmark size={14} />

            GUYANA LOTTERY

          </div>

          <h1>
            Packed Transactions
          </h1>

          <p>
            Monitor packed ticket
            activity, balances,
            sold tickets and
            outstanding payments.
          </p>

          <div className="lottery-modern-hero-actions">

            <button
              className="lottery-modern-primary-btn"
              onClick={() =>
                setShowModal(true)
              }
            >
              <Plus size={18} />
              New Packed
            </button>

          </div>

        </div>

      </div>

      {/* STATS */}

      <div className="lottery-modern-stats">

        <div className="lottery-modern-stat-card teal">

          <div className="lottery-modern-stat-icon">

            <Wallet size={22} />

          </div>

          <span>
            Total Value
          </span>

          <h2>
            $
            {totalValue.toLocaleString()}
          </h2>

        </div>

        <div className="lottery-modern-stat-card green">

          <div className="lottery-modern-stat-icon">

            <CheckCircle2 size={22} />

          </div>

          <span>
            Sold Value
          </span>

          <h2>
            $
            {soldValue.toLocaleString()}
          </h2>

        </div>

        <div className="lottery-modern-stat-card blue">

          <div className="lottery-modern-stat-icon">

            <TrendingUp size={22} />

          </div>

          <span>
            At Hand
          </span>

          <h2>
            $
            {atHandValue.toLocaleString()}
          </h2>

        </div>

        <div className="lottery-modern-stat-card red">

          <div className="lottery-modern-stat-icon">

            <CircleDollarSign
              size={22}
            />

          </div>

          <span>
            Outstanding
          </span>

          <h2>
            $
            {dueValue.toLocaleString()}
          </h2>

        </div>

      </div>

      {/* SEARCH */}

      <div className="lottery-modern-toolbar">

        <div className="lottery-modern-search">

          <Search size={18} />

          <input
            type="text"
            placeholder="Search by code or name..."
            value={searchTerm}
            onChange={(e) =>
              setSearchTerm(
                e.target.value
              )
            }
          />

        </div>

      </div>

      {/* TABLE */}

      <div className="lottery-modern-table-wrapper">

        <div className="lottery-modern-table-head">

          <span>
            Date
          </span>

          <span>
            Code
          </span>

          <span>
            Name
          </span>

          <span>
            Value
          </span>

          <span>
            Sold
          </span>

          <span>
            Paid
          </span>

          <span>
            Action
          </span>

        </div>

        {filteredTransactions.length ===
        0 ? (

          <div className="lottery-modern-empty">

            <div className="lottery-modern-empty-icon">

              <Search size={40} />

            </div>

            <h3>
              No transactions found
            </h3>

            <p>
              Try another code,
              name or create a
              new packed
              transaction.
            </p>

          </div>

        ) : (

          filteredTransactions.map(
            (txn) => (

              <div
                key={txn.id}
                className="lottery-modern-row"
              >

                <div>
                  {txn.date}
                </div>

                <div className="lottery-modern-code">

                  {txn.code}

                </div>

                <div>
                  {txn.name}
                </div>

                <div className="lottery-modern-value">

                  $
                  {Number(
                    txn.value
                  ).toLocaleString(
                    undefined,
                    {
                      minimumFractionDigits: 2,
                    }
                  )}

                </div>

                {/* SOLD */}

                <div>

                  <button
                    className={`lottery-modern-toggle ${
                      txn.sold
                        ? "active"
                        : ""
                    }`}
                    onClick={() =>
                      toggleField(
                        txn.id,
                        "sold"
                      )
                    }
                  >

                    {txn.sold
                      ? "Sold"
                      : "Pending"}

                  </button>

                  {txn.soldDate && (

                    <small>

                      {
                        txn.soldDate
                      }

                    </small>

                  )}

                </div>

                {/* PAID */}

                <div>

                  <button
                    className={`lottery-modern-toggle paid ${
                      txn.paid
                        ? "active"
                        : ""
                    }`}
                    onClick={() =>
                      toggleField(
                        txn.id,
                        "paid"
                      )
                    }
                  >

                    {txn.paid
                      ? "Paid"
                      : "Unpaid"}

                  </button>

                  {txn.paidDate && (

                    <small>

                      {
                        txn.paidDate
                      }

                    </small>

                  )}

                </div>

                {/* DELETE */}

                <div>

                  <button
                    className="lottery-modern-delete"
                    onClick={() =>
                      handleDelete(
                        txn.id
                      )
                    }
                  >

                    <Trash2 size={16} />

                  </button>

                </div>

              </div>
            )
          )

        )}

      </div>

      {/* MODAL */}

      {showModal && (

        <div className="lottery-modern-modal-overlay">

          <div className="lottery-modern-modal">

            <div className="lottery-modern-modal-header">

              <h2>
                New Packed Transaction
              </h2>

              <button
                onClick={() =>
                  setShowModal(
                    false
                  )
                }
              >

                <XCircle size={18} />

              </button>

            </div>

            <div className="lottery-modern-form-grid">

              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={
                  handleChange
                }
              />

              <input
                type="number"
                name="value"
                placeholder="Value"
                value={formData.value}
                onChange={
                  handleChange
                }
              />

              <input
                type="text"
                name="code"
                placeholder="Code"
                value={formData.code}
                onChange={
                  handleChange
                }
              />

              <input
                type="text"
                name="name"
                placeholder="Name"
                value={formData.name}
                onChange={
                  handleChange
                }
              />

            </div>

            <div className="lottery-modern-modal-actions">

              <button
                className="save"
                onClick={
                  addTransaction
                }
              >
                Save Transaction
              </button>

              <button
                className="cancel"
                onClick={() =>
                  setShowModal(
                    false
                  )
                }
              >
                Cancel
              </button>

            </div>

          </div>

        </div>

      )}

    </div>
  );
};
export default GuyanaLottery;